/**
 * The customer-visible conversation on a ticket.
 *
 * Reads go through ticket_comments_customer_view, which projects a display name
 * rather than an author id — a customer learns that PazNetworks replied, not
 * which engineer typed it. Writes go to the base table, where RLS admits only a
 * comment the caller authored on a ticket their own account owns.
 *
 * Staff in view-as mode are read-only, the same rule the visit planner applies:
 * a reply posted while impersonating would be published in the customer's name.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useViewAs } from '../contexts/ViewAsContext';

/** The bucket's own ceiling is 50 MB; a ticket attachment is a photo or a log. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export const TICKET_COMMENT_COLUMNS = `
  id, ticket_id, customer_id, author_kind, author_name, body, document_id, created_at,
  document:documents(id, title, s3_key, mime_type, file_size_bytes)
`;

/** The attachment a comment carries, if any. */
export interface TicketAttachment {
  id: string;
  title: string;
  s3_key: string;
  mime_type: string | null;
  file_size_bytes: number | null;
}

export interface TicketComment {
  id: string;
  ticket_id: string;
  customer_id: string;
  author_kind: 'customer' | 'staff';
  author_name: string;
  body: string;
  document_id: string | null;
  attachment: TicketAttachment | null;
  created_at: string;
}

export interface TicketCommentsResult {
  comments: TicketComment[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** True while the realtime channel is subscribed. */
  streaming: boolean;
  /** When the thread was last read successfully, for the freshness chip. */
  lastSyncedAt: string | null;
  readOnly: boolean;
  /**
   * Resolves true when the reply was saved, false when it was not. It never
   * rejects — the error is already surfaced through `postError` — but callers
   * need the outcome, because clearing the composer after a failure throws away
   * what the customer typed.
   */
  post: (body: string, attachment?: File | null) => Promise<boolean>;
  isPosting: boolean;
  postError: string | null;
}

function toAttachment(raw: unknown): TicketAttachment | null {
  // PostgREST returns an embedded row as an object, or an array on some
  // relationship shapes; neither is guaranteed when the column is null.
  const row = Array.isArray(raw) ? raw[0] : raw;
  if (!row || typeof row !== 'object') return null;
  const record = row as Record<string, unknown>;
  if (typeof record.id !== 'string' || typeof record.s3_key !== 'string') return null;
  return {
    id: record.id,
    title: typeof record.title === 'string' ? record.title : record.s3_key.split('/').pop() ?? 'Attachment',
    s3_key: record.s3_key,
    mime_type: typeof record.mime_type === 'string' ? record.mime_type : null,
    file_size_bytes: typeof record.file_size_bytes === 'number' ? record.file_size_bytes : null,
  };
}

export function useTicketComments(
  ticketId: string | undefined,
  customerId: string | null | undefined,
): TicketCommentsResult {
  const { isStaff, viewAsUserId } = useViewAs();
  const readOnly = isStaff && !!viewAsUserId;
  const queryClient = useQueryClient();
  const [streaming, setStreaming] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const ticketRef = useRef(ticketId);
  ticketRef.current = ticketId;

  const query = useQuery({
    queryKey: ['ticket-comments', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<TicketComment[]> => {
      const { data, error } = await supabase
        .from('ticket_comments_customer_view')
        .select(TICKET_COMMENT_COLUMNS)
        .eq('ticket_id', ticketId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []).flatMap((row) => {
        if (!row.id || !row.ticket_id || !row.body || !row.created_at) return [];
        return [{
          id: row.id,
          ticket_id: row.ticket_id,
          customer_id: row.customer_id ?? '',
          author_kind: row.author_kind === 'staff' ? 'staff' : 'customer',
          author_name: row.author_name ?? 'Account user',
          body: row.body,
          document_id: row.document_id ?? null,
          attachment: toAttachment((row as { document?: unknown }).document),
          created_at: row.created_at,
        }];
      });
    },
    staleTime: 15 * 1000,
  });

  useEffect(() => {
    if (!ticketId) {
      setStreaming(false);
      return;
    }
    const channel = supabase
      .channel(`ticket-comments:${ticketId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'ticket_comments', filter: `ticket_id=eq.${ticketId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['ticket-comments', ticketRef.current] });
        },
      )
      .subscribe((status) => setStreaming(status === 'SUBSCRIBED'));

    return () => {
      setStreaming(false);
      void supabase.removeChannel(channel);
    };
  }, [queryClient, ticketId]);

  const mutation = useMutation({
    mutationFn: async ({ body, attachment }: { body: string; attachment?: File | null }) => {
      if (readOnly) throw new Error('read_only');
      if (!ticketId) throw new Error('no_ticket');
      if (!customerId) throw new Error('no_account');
      const trimmed = body.trim();
      if (!trimmed) throw new Error('empty');

      // users.id for this session — the insert policy requires the author to be
      // the caller, and the session only carries an auth user id.
      const { data: authorId, error: whoError } = await supabase.rpc('get_my_user_id');
      if (whoError) throw whoError;
      if (!authorId) throw new Error('no_profile');

      let documentId: string | null = null;
      if (attachment) {
        if (attachment.size > MAX_ATTACHMENT_BYTES) throw new Error('too_large');

        // The path is the authorisation: the storage policy admits a write only
        // under tickets/<one of my customer ids>/, so it is built from the
        // account this ticket belongs to rather than from anything user-typed.
        const safeName = attachment.name.replace(/[^\w.\-]+/g, '_').slice(-80);
        const key = `tickets/${customerId}/${ticketId}/${Date.now()}-${safeName}`;

        const upload = await supabase.storage
          .from('documents')
          .upload(key, attachment, { contentType: attachment.type || undefined, upsert: false });
        if (upload.error) throw upload.error;

        const { data: document, error: documentError } = await supabase
          .from('documents')
          .insert({
            customer_id: customerId,
            category: attachment.type.startsWith('image/') ? 'photo' : 'other',
            title: attachment.name.slice(0, 160),
            s3_key: key,
            file_size_bytes: attachment.size,
            mime_type: attachment.type || null,
            is_customer_visible: true,
            uploaded_by_user_id: authorId,
          })
          .select('id')
          .single();
        if (documentError) throw documentError;
        documentId = document?.id ?? null;
      }

      const { error } = await supabase.from('ticket_comments').insert({
        ticket_id: ticketId,
        customer_id: customerId,
        author_user_id: authorId,
        author_kind: 'customer',
        body: trimmed,
        document_id: documentId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setPostError(null);
      queryClient.invalidateQueries({ queryKey: ['ticket-comments', ticketId] });
    },
    onError: (error) => {
      setPostError(error instanceof Error ? error.message : String(error));
    },
  });

  return {
    comments: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    streaming,
    lastSyncedAt: query.dataUpdatedAt ? new Date(query.dataUpdatedAt).toISOString() : null,
    readOnly,
    post: async (body: string, attachment?: File | null) => {
      try {
        await mutation.mutateAsync({ body, attachment });
        return true;
      } catch {
        return false;
      }
    },
    isPosting: mutation.isPending,
    postError,
  };
}
